import { FilterType, IFilter, IFilterValue, IPagedListAction } from "../../components/UI/Declarations";

/** Standard All / Yes / No options for boolean column filters. */
export const booleanSelectOptions: Record<string, boolean | null> = {
    All: null,
    Yes: true,
    No: false,
};

export function booleanSelectFilter(
    title: string,
    member: string,
    defaultValue: boolean | null = null
): IFilter {
    return {
        title,
        member,
        type: FilterType.select,
        options: { ...booleanSelectOptions },
        defaultValue,
    } as IFilter;
}

export function deletedSelectFilter(): IFilter {
    return booleanSelectFilter("Deleted", "isDeleted", false);
}

/** Pair of from/to date filters for an inclusive date range on one column. */
export function dateRangeFilters(
    label: string,
    fromMember: string,
    toMember: string
): [IFilter, IFilter] {
    return [
        { title: `${label} from`, member: fromMember, type: FilterType.date, defaultValue: null } as IFilter,
        { title: `${label} to`, member: toMember, type: FilterType.date, defaultValue: null } as IFilter,
    ];
}

export type FilterMigration = {
    member: string;
    mapValue: (value: unknown) => unknown;
};

/** Synchronous read of persisted filter values (avoids one-render default-then-stored lag). */
export function readPersistedFilterValues(
    persistenceKey: string | undefined,
    filterMigrations?: Record<string, FilterMigration>
): IFilterValue[] | null {
    if (!persistenceKey) {
        return null;
    }
    try {
        const json = localStorage.getItem(`${persistenceKey}-filters`);
        if (json === null) {
            return null;
        }
        const parsed = JSON.parse(json) as IFilterValue[];
        if (!Array.isArray(parsed)) {
            return null;
        }
        return migrateStoredFilterValues(parsed, filterMigrations);
    } catch {
        return null;
    }
}

/** Renames filter members and maps values before merge (e.g. includeDeleted → isDeleted). */
export function migrateStoredFilterValues(
    stored: IFilterValue[],
    migrations?: Record<string, FilterMigration>
): IFilterValue[] {
    if (!migrations) {
        return stored;
    }
    return stored.map((item) => {
        const migration = migrations[item.filter.member];
        if (!migration) {
            return item;
        }
        return {
            filter: { ...item.filter, member: migration.member },
            value: migration.mapValue(item.value),
        };
    });
}

/** Builds filter state from current filter definitions and optional persisted values. */
export function mergePersistedFilters(filters: IFilter[], stored: IFilterValue[] | null): IFilterValue[] {
    if (!stored || stored.length === 0) {
        return filters.map((filter) => ({
            filter,
            value: filter.defaultValue ?? null,
        }));
    }

    const storedByMember = new Map<string, unknown>();
    for (const item of stored) {
        const member = item.filter?.member;
        if (member) {
            storedByMember.set(member, item.value);
        }
    }

    return filters.map((filter) => ({
        filter,
        value: storedByMember.has(filter.member)
            ? storedByMember.get(filter.member)
            : (filter.defaultValue ?? null),
    }));
}

export const getRowClassName = (item: any, defaultRowClassName: string, rowFormatter?: (item: any) => string | null): string => {
    if (rowFormatter) {
        const customClassName = rowFormatter(item);
        if (customClassName) {
            return customClassName;
        }
    }
    return defaultRowClassName;
};

export const separateActions = (itemActions: IPagedListAction[]) => {
    const primaryActions = itemActions.filter((action) => action.primary === true);
    const secondaryActions = itemActions.filter((action) => action.primary !== true);
    return { primaryActions, secondaryActions };
};

export const buildPagesArray = (offset: number, total: number, pageSize: number): number[] => {
    const pageCount = Math.ceil(total / pageSize);
    const currentPageNumber = Math.floor(offset / pageSize) + 1;
    const pages: number[] = [];

    if (pageCount <= 7) {
        for (let i = 1; i <= pageCount; i++) {
            pages.push(i);
        }
    } else {
        if (currentPageNumber <= 4) {
            for (let i = 1; i <= 5; i++) {
                pages.push(i);
            }
            pages.push(-1);
            pages.push(pageCount);
        } else if (currentPageNumber >= pageCount - 3) {
            pages.push(1);
            pages.push(-1);
            for (let i = pageCount - 4; i <= pageCount; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            pages.push(-1);
            for (let i = currentPageNumber - 1; i <= currentPageNumber + 1; i++) {
                pages.push(i);
            }
            pages.push(-1);
            pages.push(pageCount);
        }
    }

    return pages;
};
